/**
 * Design tokens for the React Native Reusables apps.
 *
 * The className vocabulary (`bg-card`, `text-muted-foreground`) resolves through
 * the CSS variables in `global.css`, which Tailwind owns. This module holds the
 * same palette as JavaScript values for the places a class name cannot reach:
 * SVG chart fills, the QR encoder's foreground colour, and the document
 * background painted before first render.
 *
 * Because the two representations must agree, {@link toHslTriplet} exists so the
 * parity test can compare them, and `theme.test.ts` fails if `global.css` and
 * this file drift apart.
 *
 * The palette is the one the product already shipped: shadcn's neutral scale
 * with a single brand orange, plus the status tones the order chips need.
 */

/** Colour roles, named by purpose rather than by hue. */
export interface Palette {
  background: string;
  foreground: string;

  card: string;
  cardForeground: string;

  /** Floating surfaces: modals, tooltips. */
  popover: string;
  popoverForeground: string;

  /** The one accent colour: primary actions, active states, chart series 1. */
  primary: string;
  primaryForeground: string;
  /** Text and icons placed on `primarySoft`. */
  primaryStrong: string;
  /** Tinted primary background: chips, selected rows. */
  primarySoft: string;

  /** Neutral, low-emphasis fill for secondary actions. */
  secondary: string;
  secondaryForeground: string;

  /** Inset surfaces: wells, skeleton fills, inactive controls. */
  muted: string;
  mutedForeground: string;
  /** A third, quieter tier below `mutedForeground`. */
  subtleForeground: string;

  /** Hover and highlight fill; neutral, not brand-tinted. */
  accent: string;
  accentForeground: string;

  destructive: string;
  destructiveForeground: string;
  destructiveSoft: string;

  border: string;
  /** Control borders, which sit slightly stronger than layout borders. */
  input: string;
  /** Focus ring. */
  ring: string;

  /** Status tones beyond destructive, which shadcn does not define. */
  success: string;
  successSoft: string;
  info: string;
  infoSoft: string;
  progress: string;
  progressSoft: string;
  warning: string;
  warningSoft: string;

  /** Dashboard chart series; ordered by visual weight. */
  chart: string[];
}

/** Light palette. Neutrals are pure greys so the food photography carries the warmth. */
export const lightPalette: Palette = {
  background: '#FFFFFF',
  foreground: '#09090B',

  card: '#FFFFFF',
  cardForeground: '#09090B',

  popover: '#FFFFFF',
  popoverForeground: '#09090B',

  primary: '#E85D2F',
  primaryForeground: '#FAFAFA',
  primaryStrong: '#C2410C',
  primarySoft: '#FFF1EC',

  secondary: '#F4F4F5',
  secondaryForeground: '#18181B',

  muted: '#F4F4F5',
  mutedForeground: '#71717A',
  subtleForeground: '#A1A1AA',

  accent: '#F4F4F5',
  accentForeground: '#18181B',

  destructive: '#DC2626',
  destructiveForeground: '#FAFAFA',
  destructiveSoft: '#FEF2F2',

  border: '#E4E4E7',
  input: '#E4E4E7',
  ring: '#A1A1AA',

  success: '#15803D',
  successSoft: '#F0FDF4',
  info: '#1D4ED8',
  infoSoft: '#EFF6FF',
  progress: '#A16207',
  progressSoft: '#FEFCE8',
  warning: '#B45309',
  warningSoft: '#FFFBEB',

  chart: ['#E85D2F', '#2563EB', '#16A34A', '#CA8A04', '#9333EA', '#0891B2'],
};

/**
 * Dark palette. `card` equals `background` here, which is the defining trait of
 * the dark shadcn look: cards are separated by their border, not by a fill.
 */
export const darkPalette: Palette = {
  background: '#09090B',
  foreground: '#FAFAFA',

  card: '#17171C',
  cardForeground: '#FAFAFA',

  popover: '#17171C',
  popoverForeground: '#FAFAFA',

  primary: '#F2703F',
  primaryForeground: '#18181B',
  primaryStrong: '#FDBA8C',
  primarySoft: '#2A1A12',

  secondary: '#27272A',
  secondaryForeground: '#FAFAFA',

  muted: '#27272A',
  mutedForeground: '#AFAFB6',
  subtleForeground: '#85858E',

  accent: '#27272A',
  accentForeground: '#FAFAFA',

  destructive: '#7F1D1D',
  destructiveForeground: '#FAFAFA',
  destructiveSoft: '#2A1214',

  border: '#2D2D34',
  input: '#2D2D34',
  ring: '#71717A',

  success: '#4ADE80',
  successSoft: '#0F2417',
  info: '#60A5FA',
  infoSoft: '#111C2E',
  progress: '#FACC15',
  progressSoft: '#2A2208',
  warning: '#FBBF24',
  warningSoft: '#2A1F08',

  chart: ['#F2703F', '#60A5FA', '#4ADE80', '#FACC15', '#C084FC', '#22D3EE'],
};

/** Light or dark, after the stored preference has been resolved. */
export type ThemeMode = 'light' | 'dark';

/**
 * Converts a hex colour to the space-separated HSL triplet the CSS variables use,
 * which is the form `hsl(var(--token))` in the Tailwind preset expects.
 *
 * @param hex Colour as `#RRGGBB`.
 * @returns Hue, saturation, and lightness as `"H S% L%"`.
 */
export function toHslTriplet(hex: string): string {
  const clean = hex.replace('#', '');
  const r = Number.parseInt(clean.slice(0, 2), 16) / 255;
  const g = Number.parseInt(clean.slice(2, 4), 16) / 255;
  const b = Number.parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  let hue = 0;
  if (delta !== 0) {
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`;
}

/**
 * Returns the palette for a mode.
 *
 * @param mode Light or dark.
 * @returns The palette in force.
 */
export function paletteFor(mode: ThemeMode): Palette {
  return mode === 'dark' ? darkPalette : lightPalette;
}

/** Semantic colour roles a status or callout can request. */
export type Tone = 'neutral' | 'brand' | 'success' | 'info' | 'progress' | 'warning' | 'danger';

export interface ToneColors {
  /** Text and icon colour that reads against `soft`. */
  strong: string;
  /** Tinted background for chips and callouts. */
  soft: string;
  /** Solid fill, for filled badges and chart bars. */
  solid: string;
}

/**
 * Resolves a tone to its JavaScript colour values, for callers that cannot use a
 * class name — SVG fills, primarily.
 *
 * @param palette The palette in force.
 * @param tone Semantic role.
 * @returns Foreground, tinted background, and solid fill for that role.
 */
export function toneColors(palette: Palette, tone: Tone): ToneColors {
  switch (tone) {
    case 'brand':
      return { strong: palette.primaryStrong, soft: palette.primarySoft, solid: palette.primary };
    case 'success':
      return { strong: palette.success, soft: palette.successSoft, solid: palette.success };
    case 'info':
      return { strong: palette.info, soft: palette.infoSoft, solid: palette.info };
    case 'progress':
      return { strong: palette.progress, soft: palette.progressSoft, solid: palette.progress };
    case 'warning':
      return { strong: palette.warning, soft: palette.warningSoft, solid: palette.warning };
    case 'danger':
      return {
        strong: palette.destructive,
        soft: palette.destructiveSoft,
        solid: palette.destructive,
      };
    case 'neutral':
      return {
        strong: palette.mutedForeground,
        soft: palette.muted,
        solid: palette.subtleForeground,
      };
  }
}

/**
 * Tailwind classes per tone, as whole literal strings.
 *
 * Tailwind scans source text for class names, so a name assembled at runtime
 * (`bg-${tone}-soft`) would never be generated. Every variant in every component
 * therefore goes through a lookup like this one.
 */
export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-muted-foreground',
  brand: 'text-primary-strong',
  success: 'text-success',
  info: 'text-info',
  progress: 'text-progress',
  warning: 'text-warning',
  danger: 'text-destructive',
};

/** Chip and callout backgrounds per tone. */
export const TONE_SOFT: Record<Tone, string> = {
  neutral: 'bg-muted',
  brand: 'bg-primary-soft',
  success: 'bg-success-soft',
  info: 'bg-info-soft',
  progress: 'bg-progress-soft',
  warning: 'bg-warning-soft',
  danger: 'bg-destructive-soft',
};

/** Solid fills per tone, for filled badges and chart bars. */
export const TONE_SOLID: Record<Tone, string> = {
  neutral: 'bg-subtle-foreground',
  brand: 'bg-primary',
  success: 'bg-success',
  info: 'bg-info',
  progress: 'bg-progress',
  warning: 'bg-warning',
  danger: 'bg-destructive',
};

/** Solid fills per tone, as JavaScript colours for chart rectangles. */
export const TONE_SOLID_HEX: Record<Tone, keyof Palette> = {
  neutral: 'subtleForeground',
  brand: 'primary',
  success: 'success',
  info: 'info',
  progress: 'progress',
  warning: 'warning',
  danger: 'destructive',
};

/** Font stack that renders acceptably on web, iOS, and Android without a webfont. */
export const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang TC", "Microsoft JhengHei", "Helvetica Neue", Arial, sans-serif';

/** Monospace stack for order codes, which are read aloud and compared character by character. */
export const MONO_FAMILY = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

/**
 * Numeric spacing scale.
 *
 * Screens express spacing with Tailwind classes; this scale exists for the few
 * values a class cannot carry, such as a measured column width or a chart inset.
 */
export const SPACING = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32, xxxl: 48 } as const;

/** Keys of the numeric spacing scale. */
export type SpaceKey = keyof typeof SPACING;
